/**
 * Recipient shares (Phase 5 — close the loop).
 * An org shares a specific report view with named recipients and requests a
 * response. Backed by /api/outreach-unified recipient-share endpoints.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';

export type ShareDelivery = 'email' | 'in_app' | 'both' | 'link' | 'personal_email';

/** Prefilled draft returned for personal-email shares (R6) — the user sends it
 * from their own client; Capitol Commons only packages. */
export interface ShareDraft {
  subject: string;
  body: string;
}

export interface RecipientShare {
  id: string;
  view_id: string;
  outreach_item_id: string;
  recipient_name: string;
  recipient_email: string;
  recipient_source_type: 'legislator' | 'candidate' | 'officeholder' | 'manual';
  recipient_user_id: string | null;
  respond_token: string;
  respond_url: string;
  delivery: ShareDelivery;
  message: string | null;
  created_by: string;
  created_by_name?: string | null;
  view_name?: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  email_sent_at: string | null;
  in_app_notified_at: string | null;
  viewed_at: string | null;
  responded_at: string | null;
  created_at: string;
  /** Set when a Capitol Commons send threw; NULL for link/personal_email
   *  shares, which CC never sends. Cleared if a later send succeeds. */
  delivery_failed_at?: string | null;
  delivery_error?: string | null;
  response_id?: string | null;
  response_status?: string | null;
  /** R12: the district this share is scoped to; null = full report */
  scoped_district?: string | null;
  /** Present on personal-email shares from the create response (R6) */
  draft?: ShareDraft;
  /** View share token, for building the recipient-scoped export URL */
  view_share_token?: string | null;
}

export interface ShareRecipientInput {
  name: string;
  email: string;
  source_type?: 'legislator' | 'candidate' | 'officeholder' | 'manual';
  source_id?: string;
  /** R13: per-recipient message override of the shared message */
  message?: string;
}

export interface CreateSharesPayload {
  recipients: ShareRecipientInput[];
  channels: { email?: boolean; in_app?: boolean };
  message?: string;
  /** D1: 'cc' (default) = Capitol Commons delivers; 'personal' = packaged drafts only */
  delivery_mode?: 'cc' | 'personal';
  /** D5: 'full' (default) or 'district' — legislator recipients get their district only */
  scope?: 'full' | 'district';
}

export const recipientSharesQueryKey = (itemId: string) => ['recipient-shares', itemId];

export function useRecipientShares(itemId: string) {
  const { data, isLoading, error, refetch } = useQuery(
    recipientSharesQueryKey(itemId),
    async () => {
      const response = await api.get(`/outreach-unified/items/${itemId}/recipient-shares`);
      return response.data.data as RecipientShare[];
    },
    {
      enabled: !!itemId,
      staleTime: 30 * 1000
    }
  );

  return { shares: data || [], isLoading, error, refetch };
}

export async function createRecipientShares(
  viewId: string,
  payload: CreateSharesPayload
): Promise<RecipientShare[]> {
  const response = await api.post(`/outreach-unified/views/${viewId}/recipient-shares`, payload);
  return response.data.data as RecipientShare[];
}

export function useRevokeRecipientShare(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (shareId: string) => {
      await api.post(`/outreach-unified/recipient-shares/${shareId}/revoke`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(recipientSharesQueryKey(itemId));
        toast.success('Share link revoked');
      },
      onError: () => {
        toast.error('Failed to revoke share');
      }
    }
  );
}
